import React, { useEffect } from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import styles from './ConversationPanel.module.css';
import { type MessageProps } from './MessageContent';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import { AhaButton } from 'components/common/reactifyWebComponent';

const ErrorDisplay = ApplicationView(({ error }: { error: string }) => {
  const controller = useController<AssistantViewController>();

  const fixError = () => {
    // We got an error from the app - ask the LLM to solve it for us.
    controller.actionSubmitMessage(
      `The prototype encountered this error:\n\n    ${error}\n\nPlease understand the problem and fix it`
    );
  };

  return (
    <>
      <aha-empty-state>
        <h2 slot='title'>Something went wrong</h2>
        <i
          slot='icon'
          className='far fa-face-disappointed pb-3'
          style={{ fontSize: 25, color: 'var(--aha-gray-500)' }}
        />
        <p>The prototype encountered an error:</p>
        <p>
          <pre>
            <code>{error}</code>
          </pre>
        </p>

        <AhaButton
          slot='actions'
          kind='secondary'
          size='medium'
          onClick={() => fixError()}
        >
          Try to fix automatically
        </AhaButton>
      </aha-empty-state>
    </>
  );
});

const ReactContent = ({ body }: MessageProps) => {
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // We use a random subdomain for each app to avoid any cookies from being
  // shared between apps or with aha.io.
  const randomSubdomain = Math.random()
    .toString(36)
    .substring(2, 10 + 2);
  const targetOrigin = `https://${randomSubdomain}.${window.Configatron?.userContentDomain}`;

  // Listen for messages from the iframe.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== targetOrigin) return;

      const error = event.data.error;
      if (error) {
        setErrorMessage(error);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });

  if (!window.currentAccount?.aiAllowScriptableArtifacts) {
    return (
      <aha-empty-state>
        <h2 slot='title'>Content type disabled</h2>
        Scriptable content types are disabled for your account. Please contact
        your account administrator.
      </aha-empty-state>
    );
  }

  if (errorMessage) {
    // TODO: This is where we would show a warning to the user before loading the
    // app for the first time.

    return <ErrorDisplay error={errorMessage} />;
  } else {
    return (
      <iframe
        className={styles.htmlContent}
        onLoad={e => {
          e.currentTarget.removeAttribute('srcdoc');
          e.currentTarget.contentWindow?.postMessage({ body }, targetOrigin);
        }}
        src={`${targetOrigin}/assistant/sandbox/react?acceptOrigin=${encodeURIComponent(
          window.location.origin
        )}`}
      />
    );
  }
};

export default ApplicationView(ReactContent);
